import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getCountryFlagSvgUrl } from '../api/fetchDataFromCDN';
import { useSettings } from '../hooks/useSettings';

const DELAY_TIME = 0.5;
let grayscale = false;

const Grid = styled.div<{ end?: boolean }>`
  transition: 1s;
  transition-delay: ${DELAY_TIME}s;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto 1fr;
  grid-gap: ${(props) => (props.end ? '0px' : '2px')};
  z-index: ${(props) => (props.end ? 2 : 1)};
  width: fit-content;
  // filter: grayscale(${(grayscale && '100%') || '0%'});
  border: 2px solid #dddddd;
`;

const TileFront = styled.div`
  width: 100%;
  height: 100%;
  justify-content: center;
  background: #dddddd;
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  // Prevent Safari tab preview from spoiling flag
  z-index: 1;
  opacity: 1;
`;

const Tile = styled.div<{
  flagWidth: number;
  height: number;
  rotate: string;
}>`
  transition: transform 1s;
  transform-style: preserve-3d;
  display: flex;
  justify-content: center;
  padding: ${(props) =>
    `${props.height ? `${props.height / 2}px` : '2rem'} ${Math.floor(
      props.flagWidth / 6,
    )}px`};
  position: relative;
  transform: ${(props) =>
    props.rotate === 'true' ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const FlagImage = styled.img<{
  flag: string;
  width: number;
  left: number;
  top: number;
}>`
  content: url(${(props) => props.flag});
  position: relative;
  width: ${(props) => `${props.width}px`};
  left: ${(props) => `${props.left}px`};
  top: ${(props) => `${props.top}px`};
  max-width: unset;
  height: ${(props) => `${props.height}px`};
`;

const FlagContainer = styled.div<{ width: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${({ width }) => (width * 2) / 3}px; // 3:2 ratio
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`;

export function FlagGrid({
  end,
  countryInfo,
  flippedArray,
  flagUrl,
}: {
  end: boolean;
  countryInfo: { code: string };
  flippedArray: boolean[];
  flagUrl?: string;
}) {
  const resolvedFlagUrl = flagUrl ?? getCountryFlagSvgUrl(countryInfo.code);
  const flagWidth = useDynamicFlagWidth();
  const [flagLoad, setFlagLoad] = useState(false);
  const [scaledFlagHeight, setScaledFlagHeight] = useState(0);

  const [settings] = useSettings();
  grayscale = settings?.grayScale;

  useEffect(() => {
    // create invisible element to get accurate flag height
    const img = new Image();
    img.src = resolvedFlagUrl;
    img.width = flagWidth;
    img.style.visibility = 'hidden';
    img.onload = () => {
      setScaledFlagHeight(img.height);
      setFlagLoad(true);
      img.remove();
    };
    document.body.appendChild(img);
    return () => {
      img.onload = null;
      img.remove();
    };
  }, [resolvedFlagUrl, flagWidth]);

  return (
    <FlagContainer width={flagWidth}>
      {flagLoad ? (
        <Grid
          end={end}
          style={{
            filter: `grayscale(${(settings?.grayScale && '100%') || '0%'})`,
          }}
        >
          {flippedArray.map((flipped, n) => (
            <Tile
              key={n}
              rotate={flipped && flagLoad ? 'true' : 'false'}
              height={scaledFlagHeight / 2}
              flagWidth={flagWidth}
            >
              <TileFront></TileFront>
              <div
                className="transition duration-1000 w-full h-full justify-center bg-white absolute top-0 overflow-hidden dark:bg-[#121212]"
                style={{
                  transitionDelay: `${DELAY_TIME}s`,
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                }}
              >
                <FlagImage
                  flag={resolvedFlagUrl}
                  width={flagWidth}
                  left={-Math.floor((n % 3) * (flagWidth / 3))}
                  top={-((Math.floor(n / 3) * scaledFlagHeight) / 2)}
                ></FlagImage>
              </div>
            </Tile>
          ))}
        </Grid>
      ) : null}
    </FlagContainer>
  );
}

function useDynamicFlagWidth() {
  const [width, setWidth] = useState(300);

  useEffect(() => {
    function handleResize() {
      const newWidth = window.innerWidth - 20;
      setWidth(Math.min(newWidth, 500)); // max width for flag is 500px
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return width;
}
